import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  FlatList,
  RefreshControl,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppSelector, useAppDispatch } from '@/store';
import {
  useGetThreadsQuery,
  useToggleSaveThreadMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { clearDeletedThreads, trackDeletedThread } from '@/store/slices/residents/corkBoardUISlice';
import { useCorkBoardMutation } from '@/hooks/residents/corkBoard/useCorkBoardMutation';
import { useThreadActions } from '@/hooks/residents/corkBoard/useThreadActions';
import { useToast } from '@/contexts/admin/ToastContext';
import { useAccumulatedPaging } from '@/hooks/common/useAccumulatedPaging';
import { useCorkBoardFilters } from '@/hooks/residents/corkBoard/useCorkBoardFilters';
import {
  ThreadCardSkeleton,
  CorkBoardEmptyState,
} from '@/components/residents/corkBoard';
import {
  PageHeader,
  NewsfeedThreadCard,
  NewsfeedSearchBar,
} from '@/components/residents/redesign';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import { FilterChipGroup } from '@/components/shared/redesign';
import {
  matchesSurveyFilter,
  SURVEY_STATUS_FILTER_OPTIONS,
  type SurveyStatusFilter,
} from '@/utils/residents/surveys/surveyStatusFilter';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { useResponsive } from '@/hooks/common/useResponsive';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import type { CorkBoardThreadSummary } from '@/types/residents/corkBoard';

type SurveysNavigationProp = StackNavigationProp<RootStackParamList>;

const HP = REDESIGN_SPACING.horizontalPadding;
// Match the marketplace desktop content frame.
const MAX_CONTENT_WIDTH = 1200;

export const SurveysListScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<SurveysNavigationProp>();
  const route = useRoute();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode !== 'mobile' && layoutMode !== 'tablet';
  // Only show the back link when reached from a Home quick-action card (which
  // passes showBackLink). Reaching Surveys from the desktop sidebar leaves the
  // content stack with history too, so canGoBack() alone can't tell them apart.
  const showBackLink = (route.params as { showBackLink?: boolean } | undefined)?.showBackLink === true;
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const propertyId = user?.propertyId;
  const propertyName = user?.property?.marketingName || user?.property?.legalName || '';
  const deletedThreadIds = useAppSelector((state) => state.corkBoardUI.deletedThreadIds);
  const insets = useSafeAreaInsets();

  const filters = useCorkBoardFilters({ propertyId, kind: 'survey' });

  const {
    currentData: threadsData,
    isLoading,
    isFetching,
    refetch,
    isError,
  } = useGetThreadsQuery(filters.queryParams, { skip: filters.shouldSkipQuery });

  const surveys = threadsData?.threads ?? [];
  const pagination = threadsData?.pagination;
  const hasMorePages = pagination ? pagination.currentPage < pagination.totalPages - 1 : false;

  const { accumulatedRows, clearIfPendingReplaceAndNoRows, markForReplace } = useAccumulatedPaging({
    rows: surveys,
    accumulationKey: filters.accumulationKey,
  });

  useEffect(() => {
    clearIfPendingReplaceAndNoRows(isLoading);
  }, [isLoading, clearIfPendingReplaceAndNoRows]);

  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<SurveyStatusFilter>('all');

  const visibleRows = useMemo(() => {
    const rows = deletedThreadIds.length > 0
      ? accumulatedRows.filter((t) => !deletedThreadIds.includes(String(t.id)))
      : accumulatedRows;
    if (statusFilter === 'all') return rows;
    const now = Date.now();
    return rows.filter((t) => matchesSurveyFilter(t.survey, statusFilter, now));
  }, [accumulatedRows, deletedThreadIds, statusFilter]);

  const isInitialLoad = isLoading && visibleRows.length === 0;
  const isLoadingMore = isFetching && filters.page > 0;

  useEffect(() => {
    if (!isFetching) setIsManualRefreshing(false);
  }, [isFetching]);

  const handlePress = useCallback((thread: CorkBoardThreadSummary) => {
    navigation.navigate('ThreadDetail', { threadId: thread.id });
  }, [navigation]);

  const { showSuccess, showError } = useToast();
  const [toggleSaveTrigger] = useToggleSaveThreadMutation();
  const { execute: executeToggleSave } = useCorkBoardMutation<string, any>(
    toggleSaveTrigger, { errorFallback: 'Failed to update bookmark.' },
  );

  const {
    handleEditThread,
    openDeleteConfirm,
    openReportModal,
    threadActionModals,
  } = useThreadActions({
    onDeleteSuccess: (thread) => dispatch(trackDeletedThread(thread.id)),
  });

  const handleSave = useCallback(async (thread: CorkBoardThreadSummary) => {
    const result = await executeToggleSave(thread.id, { silent: true });
    if (result.success) {
      const saved = result.data?.saved ?? !thread.isSaved;
      showSuccess(saved ? 'Survey saved' : 'Survey unsaved');
    } else {
      showError('Failed to save survey');
    }
  }, [executeToggleSave, showSuccess, showError]);

  const handleRefresh = useCallback(() => {
    setIsManualRefreshing(true);
    dispatch(clearDeletedThreads());
    markForReplace();
    if (filters.page === 0) {
      refetch();
    } else {
      filters.resetPage();
    }
  }, [filters, refetch, markForReplace, dispatch]);

  const handleEndReached = useCallback(() => {
    if (!isFetching && hasMorePages) {
      filters.loadNextPage();
    }
  }, [isFetching, hasMorePages, filters]);

  const renderItem = useCallback(({ item }: { item: CorkBoardThreadSummary }) => (
    <NewsfeedThreadCard
      thread={item}
      onPress={handlePress}
      onSave={handleSave}
      onEdit={handleEditThread}
      onDelete={openDeleteConfirm}
      onReport={openReportModal}
    />
  ), [handlePress, handleSave, handleEditThread, openDeleteConfirm, openReportModal]);

  const keyExtractor = useCallback((item: CorkBoardThreadSummary) => item.id, []);

  const ListHeaderComponent = useMemo(() => (
    <View style={styles.headerSection}>
      {showBackLink && <ScreenBackLink style={styles.backLink} />}
      <PageHeader title="Surveys" subtitle={propertyName} />
      <NewsfeedSearchBar
        value={filters.searchInput}
        onChangeText={filters.setSearch}
        onClear={filters.clearSearch}
      />
      <FilterChipGroup
        options={SURVEY_STATUS_FILTER_OPTIONS}
        value={statusFilter}
        onChange={setStatusFilter}
        testID="survey-status-filter"
      />
    </View>
  ), [showBackLink, propertyName, filters.searchInput, filters.setSearch, filters.clearSearch, statusFilter]);

  const ListEmptyComponent = useMemo(() => {
    if (isInitialLoad || (isFetching && visibleRows.length === 0)) return <ThreadCardSkeleton />;
    if (isError) return <CorkBoardEmptyState variant="error" onRetry={handleRefresh} />;
    if (filters.hasActiveFilters || statusFilter !== 'all') {
      return <CorkBoardEmptyState variant="noResults" />;
    }
    return <CorkBoardEmptyState variant="noThreads" />;
  }, [isInitialLoad, isFetching, visibleRows.length, isError, filters.hasActiveFilters, statusFilter, handleRefresh]);

  const ListFooterComponent = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={PRIMITIVE_COLORS.resioBlue} />
      </View>
    );
  }, [isLoadingMore]);

  return (
    <>
      <View style={styles.container}>
        <FlatList
          data={visibleRows}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          ListHeaderComponent={ListHeaderComponent}
          ListEmptyComponent={ListEmptyComponent}
          ListFooterComponent={ListFooterComponent}
          contentContainerStyle={[
            styles.content,
            isDesktop && styles.contentDesktop,
            { paddingBottom: 80 + insets.bottom },
          ]}
          ItemSeparatorComponent={ItemSeparator}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.4}
          showsVerticalScrollIndicator={false}
          removeClippedSubviews={Platform.OS !== 'web'}
          refreshControl={
            <RefreshControl
              refreshing={isManualRefreshing}
              onRefresh={handleRefresh}
              tintColor={colors.text.primary}
            />
          }
        />
      </View>
      {threadActionModals}
    </>
  );
};

const ItemSeparator = () => <View style={styles.separator} />;

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { paddingHorizontal: HP },
  contentDesktop: {
    paddingTop: HP,
    maxWidth: MAX_CONTENT_WIDTH,
    alignSelf: 'center',
    width: '100%',
  },
  headerSection: { gap: 12, paddingBottom: 8 },
  backLink: { marginBottom: -4 },
  separator: { height: 16 },
  footerLoader: { paddingVertical: 20, alignItems: 'center' },
});
